import type { INovaUserAuthHeaders } from '@nova/types';
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, isAxiosError } from 'axios';

export interface CreateGameApiHttpOptions {
  /** Invoked when this client receives a 401 (e.g. clear session and redirect). */
  onUnauthorized?: (error: unknown) => void;
}

type HeaderRecord = Record<string, string | number | boolean | undefined | null>;

function mergeAuthHeaders(config: AxiosRequestConfig | undefined, auth: INovaUserAuthHeaders | undefined): AxiosRequestConfig {
  const next: AxiosRequestConfig = { ...(config ?? {}) };
  if (!auth) {
    return next;
  }
  const prev = next.headers;
  let prevObj: HeaderRecord = {};
  if (typeof prev === 'object' && prev !== null && !Array.isArray(prev)) {
    prevObj = { ...(prev as HeaderRecord) };
  }
  next.headers = { ...prevObj, ...auth };
  return next;
}

/**
 * Per-request merge of `getHeaders()` into config — **no** global request interceptors.
 * Use for Nova REST / game API traffic only, not public CDNs.
 */
export class GameApiHttp {
  private readonly _client: AxiosInstance;

  public constructor(private readonly getHeaders: () => INovaUserAuthHeaders | undefined, options?: CreateGameApiHttpOptions) {
    this._client = axios.create();
    if (options?.onUnauthorized) {
      const onUnauthorized = options.onUnauthorized;
      this._client.interceptors.response.use(
        (response) => response,
        (error: unknown) => {
          if (isAxiosError(error) && error.response?.status === 401) {
            onUnauthorized(error);
          }
          throw error;
        }
      );
    }
  }

  /** Same underlying instance passed into `@nova/age` `Match` REST helpers (headers set per call in AGE). */
  public get raw(): AxiosInstance {
    return this._client;
  }

  public get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this._client.get<T>(url, mergeAuthHeaders(config, this.getHeaders()));
  }

  public post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this._client.post<T>(url, data, mergeAuthHeaders(config, this.getHeaders()));
  }

  public patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this._client.patch<T>(url, data, mergeAuthHeaders(config, this.getHeaders()));
  }

  public delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this._client.delete<T>(url, mergeAuthHeaders(config, this.getHeaders()));
  }
}

export function createGameApiHttp(getHeaders: () => INovaUserAuthHeaders | undefined, options?: CreateGameApiHttpOptions): GameApiHttp {
  return new GameApiHttp(getHeaders, options);
}
